import React, { useState } from "react";
import {
  makeStyles,
  Button,
  Text,
  Tooltip,
  tokens,
} from "@fluentui/react-components";
import CurrencyField from "../../../common/CurrencyField/CurrencyField";
import RenderIf from "../../../common/RenderIf/RenderIf";
import { formatToCurrency } from "../../../../utils/currency";

interface OfferPriceProps {
  onOfferPriceChange?: (offeredPrice: number) => void;
  onAccepted: (offeredPrice: number) => void;
  onDeclined: (offeredPrice: number) => void;
}

const useStyles = makeStyles({
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    gap: "8px",
    width: "100%",
  },
  actionButtons: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "8px",
    width: "100%",
  },
  label: {
    width: "126px",
    color: tokens.colorPaletteBlueForeground2,
  },
  offeredData: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    width: "100%",
  },
  offerDetails: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  success: {
    color: tokens.colorPaletteGreenForeground2,
  },
  danger: {
    color: tokens.colorPaletteRedForeground2,
  },
  price: {
    color: tokens.colorPaletteBlueForeground2,
  },
});

const OfferPrice: React.FC<OfferPriceProps> = ({
  onOfferPriceChange,
  onAccepted,
  onDeclined,
}) => {
  const styles = useStyles();
  const [offeredPrice, setOfferedPrice] = useState<number>(0);
  const [selectedAction, setSelectedAction] = useState<
    "accepted" | "declined" | undefined
  >(undefined);

  const handleAccept = () => {
    if (offeredPrice) {
      onAccepted(offeredPrice);
      setSelectedAction("accepted");
    }
  };

  const handleDecline = () => {
    if (offeredPrice) {
      onDeclined(offeredPrice);
      setSelectedAction("declined");
    }
  };

  const handleChange = (value: string) => {
    setOfferedPrice(value !== "" ? Number(value) : 0);
    onOfferPriceChange?.(value !== "" ? Number(value) : 0);
  };

  return (
    <>
      <RenderIf shouldRender={selectedAction === undefined}>
        <div className={styles.content}>
          <Text className={styles.label}>Offer Price:</Text>
          <CurrencyField
            value={offeredPrice}
            onChange={handleChange}
            placeholder="Enter offer price"
          />

          <div className={styles.actionButtons}>
            <Tooltip content="Decline Offer" relationship="label">
              <Button
                appearance="transparent"
                onClick={handleDecline}
                disabled={!offeredPrice}
              >
                <Text>Decline</Text>
              </Button>
            </Tooltip>

            <Tooltip content="Accept Offer" relationship="label">
              <Button
                appearance="primary"
                onClick={handleAccept}
                disabled={!offeredPrice}
              >
                <Text>Accept</Text>
              </Button>
            </Tooltip>
          </div>
        </div>
      </RenderIf>

      <RenderIf shouldRender={selectedAction !== undefined}>
        <div className={styles.content}>
          <div className={styles.offeredData}>
            <div className={styles.offerDetails}>
              {selectedAction === "accepted" ? (
                <Text className={styles.success}>Accepted:</Text>
              ) : (
                <Text className={styles.danger}>Declined:</Text>
              )}
              <Text className={styles.price}>
                {offeredPrice ? formatToCurrency(Number(offeredPrice)) : "0.00"}
              </Text>
            </div>
            <Button
              appearance="transparent"
              onClick={() => setSelectedAction(undefined)}
            >
              Change
            </Button>
          </div>
        </div>
      </RenderIf>
    </>
  );
};

export default OfferPrice;
